export type AttendanceCoordinates = {
  latitude: number;
  longitude: number;
};

export type AttendanceGeolocationErrorCode =
  | 'PERMISSION_DENIED'
  | 'POSITION_UNAVAILABLE'
  | 'TIMEOUT'
  | 'UNSUPPORTED';

export class AttendanceGeolocationError extends Error {
  code: AttendanceGeolocationErrorCode;

  constructor(code: AttendanceGeolocationErrorCode, message: string) {
    super(message);
    this.name = 'AttendanceGeolocationError';
    this.code = code;
  }
}

function geolocationErrorMessage(code: AttendanceGeolocationErrorCode): string {
  switch (code) {
    case 'PERMISSION_DENIED':
      return 'Location permission was denied. Allow location access for this site in your browser settings, then tap Mark attendance again.';
    case 'POSITION_UNAVAILABLE':
      return 'Your location could not be determined. Please check that location services are enabled on your device and try again.';
    case 'TIMEOUT':
      return 'Timed out while getting your location. Please try again.';
    case 'UNSUPPORTED':
    default:
      return 'Location is not supported on this device. Please enable location services and try again.';
  }
}

function mapGeolocationError(error: GeolocationPositionError): AttendanceGeolocationError {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      return new AttendanceGeolocationError('PERMISSION_DENIED', geolocationErrorMessage('PERMISSION_DENIED'));
    case error.POSITION_UNAVAILABLE:
      return new AttendanceGeolocationError(
        'POSITION_UNAVAILABLE',
        geolocationErrorMessage('POSITION_UNAVAILABLE'),
      );
    case error.TIMEOUT:
      return new AttendanceGeolocationError('TIMEOUT', geolocationErrorMessage('TIMEOUT'));
    default:
      return new AttendanceGeolocationError(
        'POSITION_UNAVAILABLE',
        geolocationErrorMessage('POSITION_UNAVAILABLE'),
      );
  }
}

function getAttendanceCoordinatesOnce(options: PositionOptions): Promise<AttendanceCoordinates> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new AttendanceGeolocationError('UNSUPPORTED', geolocationErrorMessage('UNSUPPORTED')));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        reject(mapGeolocationError(error));
      },
      options,
    );
  });
}

/**
 * Request the attendee's coordinates. Must be called from a direct user
 * gesture (e.g. a button click) so browsers will re-prompt after the user
 * changes site location settings.
 */
export async function getAttendanceCoordinatesWithRetry(): Promise<AttendanceCoordinates> {
  try {
    return await getAttendanceCoordinatesOnce({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  } catch (firstError) {
    if (
      firstError instanceof AttendanceGeolocationError &&
      firstError.code === 'PERMISSION_DENIED'
    ) {
      throw firstError;
    }
    return getAttendanceCoordinatesOnce({
      enableHighAccuracy: false,
      timeout: 15000,
      maximumAge: 60000,
    });
  }
}
